import React from "react";

import { Button } from "@mui/material";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  sendMessage,
  updateCallingNote,
  updateCallingSelect,
} from "../utils/api";
import Table from "./Table";

const CustomerDetailSubPage = ({ customerData, id, onSelectionChange }) => {
  const [checked, setChecked] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [callingSelections, setCallingSelections] = React.useState({});
  const [callingNotes, setCallingNotes] = React.useState({});

  // Initialize calling selections and notes from customerData
  React.useEffect(() => {
    const selections = {};
    const notes = {};
    customerData.forEach((item) => {
      selections[item._id] = item.callingSelected || false;
      notes[item._id] = item.callingNote || "";
    });
    setCallingSelections(selections);
    setCallingNotes(notes);
  }, [customerData]);

  const handleSend = async () => {
    if (isLoading) return;
    if (checked.length < 1)
      return toast.error("Please select at least one user");
    setIsLoading(true);
    const [success] = await sendMessage({
      data: checked,
    });
    if (success) {
      setChecked([]);
      toast.success("Message sent successfully");
    }
    setIsLoading(false);
  };

  const handleCallingSelectChange = async (customerId, isSelected) => {
    // Update local state immediately for better UX
    setCallingSelections((prev) => ({
      ...prev,
      [customerId]: isSelected,
    }));

    // Call API to update backend
    const [success] = await updateCallingSelect({
      id: customerId,
      callingSelected: isSelected,
    });

    if (!success) {
      // Revert on failure
      setCallingSelections((prev) => ({
        ...prev,
        [customerId]: !isSelected,
      }));
      toast.error("Failed to update selection");
    } else {
      // Call parent callback to refresh calling selects
      if (onSelectionChange) {
        onSelectionChange();
      }
    }
  };

  const handleCallingNoteChange = (customerId, note) => {
    // Update local state immediately
    setCallingNotes((prev) => ({
      ...prev,
      [customerId]: note,
    }));
  };

  const handleCallingNoteBlur = async (customerId, note) => {
    // Call API to update backend when user finishes typing (on blur)
    const [success] = await updateCallingNote({
      id: customerId,
      callingNote: note,
    });

    if (!success) {
      toast.error("Failed to update calling note");
    } else {
      // Call parent callback to refresh calling selects
      if (onSelectionChange) {
        onSelectionChange();
      }
    }
  };

  return (
    <div className="flex flex-col md:w-[68%]">
      {checked.length > 0 && (
        <div className="flex justify-between items-center">
          <Button onClick={handleSend} variant="contained" color="primary">
            {isLoading ? "Sending..." : `Payment Reminder`}
          </Button>
        </div>
      )}
      <Table
        columns={[
          {
            title: "Select",
            dataIndex: "select",
            render: (item) => (
              <input
                type="checkbox"
                checked={callingSelections[item._id] || false}
                onChange={(e) => {
                  handleCallingSelectChange(item._id, e.target.checked);
                }}
                className="w-4 h-4"
              />
            ),
          },
          {
            title: "Calling Note",
            dataIndex: "callingNote",
            render: (record) => (
              <input
                type="text"
                value={callingNotes[record._id] || ""}
                onChange={(e) =>
                  handleCallingNoteChange(record._id, e.target.value)
                }
                onBlur={(e) =>
                  handleCallingNoteBlur(record._id, e.target.value)
                }
                placeholder="Add note..."
                className="border border-gray-300 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              />
            ),
          },
          {
            title: "Customer Name",
            dataIndex: "name",
            render: (record) => (
              <p>
                {record?.name}{" "}
                {record?.clients?.length > 0 && (
                  <>
                    ({record?.clients?.map((item) => item.userName)?.join(",")})
                  </>
                )}
              </p>
            ),
          },
          {
            title: "Phone Number",
            dataIndex: "phoneNumber",
          },

          {
            title: "Message",
            render: (item) => (
              <input
                type="checkbox"
                checked={checked.find((i) => i.id === item._id) ? true : false}
                onChange={(e) => {
                  if (e.target.checked) {
                    setChecked([
                      ...checked,
                      {
                        id: item._id,
                        // amount: item.balance,
                      },
                    ]);
                  } else {
                    setChecked(checked.filter((i) => i.id !== item._id));
                  }
                }}
              />
            ),
          },
          {
            title: "Tag",
            dataIndex: "tag",
            td: (record) => (
              <td
                className={`border px-2 py-1 border-black ${
                  record?.wagon === 0
                    ? "bg-yellow-400"
                    : record?.tag < 0
                    ? "bg-red-500"
                    : "bg-green-500"
                }`}
              >
                <Link to={`/admin/customer?id=${record?._id}`}>
                  {record?.tag?.toFixed(2)}
                </Link>
              </td>
            ),
          },
          {
            title: "PDF",
            link: `/admin/pdf/customer/${id}`,
          },
        ]}
        data={customerData}
      />
    </div>
  );
};

export default CustomerDetailSubPage;
